import { getBalance } from "@/actions/wallet";
import { getProfile } from "@/actions/authentication";
import { Product } from "@/types/viator/product";
import { Provider1HotelType } from "@/types/provider1";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UserItineraryStoreTypes {
  role: string;
  status: "pending" | "approved";
  displayName: string;
  lastName: string;
  firstName: string;
  image: string;
  email: string;
  _id: string;
  isRegisteredAsInfluencer: boolean;

  updateStatus: (status: "pending" | "approved") => void;
  setItineraryUser: (user: {
    role: string;
    status: "pending" | "approved";
    displayName: string;
    image: string;
    email: string;
  }) => void;
}

export const useItineraryUserStore = create<UserItineraryStoreTypes>()(
  (set) => ({
    role: "",
    displayName: "",
    firstName: "",
    lastName: "",
    _id: "",
    email: "",
    image: "",
    status: "pending",
    isRegisteredAsInfluencer: false,
    updateStatus: (status) => set({ status }),
    setItineraryUser: (user) => set({ ...user }),
  }),
);

interface Referral {
  userId: string;
  status: "pending" | "approved";
}

interface UserStoreTypes {
  isChatLimited: boolean;
  isRegisteredAsInfluencer: boolean;
  isGettingProfile: boolean;
  createdAt: string;
  influencerId: string;
  referralCode: string;
  type: string;
  status: string;
  displayName: string;
  lastName: string;
  firstName: string;
  image: string;
  email: string;
  _id: string;
  approval: string;
  favoriteHotels: { [key: string]: number[] } | null;
  favoriteHotelsDetails: { [key: string]: Provider1HotelType[] } | null;
  favoriteActivities: string[] | null;
  actions: { type: string; message: string }[];
  walletBalance: number;
  setWalletBalance: (walletBalance: number) => void;
  updateUserWallet: () => Promise<void>;
  setIsChatLimited: (isChatLimited: boolean) => void;
  setIsGettingProfile: (isGettingProfile: boolean) => void;
  addActions: (actions: { type: string; message: string }) => void;
  setFavoriteHotels: (hotels: { [key: string]: number[] } | null) => void;
  setFavoriteHotelsDetails: (
    hotels: { [key: string]: Provider1HotelType[] } | null,
  ) => void;
  setFavoriteActivities: (activities: string[] | null) => void;
  setUserProfile: (user: {
    role: string;
    status: string;
    displayName: string;
    image: string;
    email: string;
    referrals: Referral[];
    point: number;
  }) => void;
  fetchUserProfile: () => Promise<void>;
  resetUserProfile: () => void;
}
const initials = {
  createdAt: "",
  type: "",
  referralCode: "",
  displayName: "",
  influencerId: "",
  firstName: "",
  lastName: "",
  _id: "",
  email: "",
  image: "",
  status: "",
  approval: "",
  favoriteHotels: null,
  walletBalance: 0,
  isRegisteredAsInfluencer: false,
  isChatLimited: false,
  isGettingProfile: true,
  actions: [],
};

export const useUserStore = create<UserStoreTypes>()(
  persist(
    (set) => ({
      createdAt: "",
      actions: [],
      type: "",
      referralCode: "",
      influencerId: "",
      displayName: "",
      firstName: "",
      lastName: "",
      _id: "",
      email: "",
      image: "",
      status: "",
      approval: "",
      favoriteHotels: null,
      favoriteHotelsDetails: null,
      favoriteActivities: null,
      walletBalance: 0,
      isRegisteredAsInfluencer: false,
      isChatLimited: false,
      isGettingProfile: false,
      setWalletBalance: (walletBalance) => set({ walletBalance }),
      addActions: (action: { type: string; message: string }) =>
        set((prev) => ({ actions: [...prev.actions, action] })),
      updateUserWallet: async () => {
        const res = await getBalance();
        const { result, response } = JSON.parse(res);

        if (response.ok) {
          set({ walletBalance: result.balance });
        } else {
          // Handle the case where fetching the balance fails, e.g., showing a message
        }
      },
      setFavoriteHotels: (hotels) => set({ favoriteHotels: hotels }),
      setFavoriteHotelsDetails: (hotels) =>
        set({ favoriteHotelsDetails: hotels }),
      setFavoriteActivities: (activities: string[] | null) =>
        set({ favoriteActivities: activities }),

      setUserProfile: (user) => set({ ...user }),
      resetUserProfile: () => set({ ...initials }),
      setIsChatLimited: (value: boolean) => set({ isChatLimited: value }),
      setIsGettingProfile: (value: boolean) => set({ isGettingProfile: value }),
      fetchUserProfile: async () => {
        set({ isGettingProfile: true });
        try {
          const res = await getProfile();
          const { result, response } = JSON.parse(res);

          if (response.ok) {
            set((state) => {
              // Reset profile first
              // Then set new data
              return {
                ...state,
                ...result.data,
                _id: result.data.userId,
                image: result.data.image || result.data.avatar || null,
                isGettingProfile: false,
              };
            });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        } finally {
          set({ isGettingProfile: false });
        }
      },
    }),
    {
      name: "user-store",
      partialize: (state) => ({ isChatLimited: state.isChatLimited }),
    },
  ),
);
